import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import { Modal, Button } from '@/components/ui';
import { blockTradeApi } from '@/services/api';
import { formatCurrency, formatNumber, formatPercentage } from '@/utils/formatters';
import {
  asSnapshot,
  BlockTrade,
  BlockTradeOverridePayload,
  BlockTradeSnapshot,
  OverridableMetric,
} from '@/types/blockTrade';
import styles from './BlockTradeForm.module.css';

interface OverridesFormValues {
  limit_price: string;
  limit_price_note: string;
  mandate_qty: string;
  mandate_qty_note: string;
  target_position_pct: string;
  target_position_pct_note: string;
}

interface BlockTradeOverridesModalProps {
  open: boolean;
  blockTrade: BlockTrade;
  onClose: () => void;
  onSaved: (saved: BlockTrade) => void;
}

/** The three desk inputs the operator owns, in the order the detail page shows them.
 *  `sourced` reads the figure the system arrived at, so the form can show what a typed
 *  number is standing in for rather than presenting an empty box with no context.
 *  `allowsZero` marks the one field where zero is a figure the desk means rather than a
 *  slip: a 0% target position says "hold none of this name". A zero price or zero mandate
 *  size could only ever be a mistype, so those stay positive. Mirrors the server's rule. */
const OVERRIDABLE_FIELDS: {
  key: OverridableMetric;
  label: (isOurBuy: boolean) => string;
  step: string;
  allowsZero: boolean;
  placeholder: string;
  sourced: (snapshot: BlockTradeSnapshot | null) => number | null | undefined;
  format: (value: number) => string;
}[] = [
  {
    key: 'limit_price',
    label: isOurBuy => (isOurBuy ? 'Our Buy Limit Price' : 'Our Sell Limit Price'),
    step: '0.01',
    allowsZero: false,
    placeholder: 'e.g. 386',
    sourced: snapshot => snapshot?.desk_mandate?.limit_price?.value,
    format: value => formatCurrency(value, undefined, 2),
  },
  {
    key: 'mandate_qty',
    label: () => 'Our Total Mandate Qty (shares)',
    step: '1',
    allowsZero: false,
    placeholder: 'e.g. 417000',
    sourced: snapshot => snapshot?.desk_mandate?.mandate_qty?.value,
    format: value => `${formatNumber(value, 0)} shares`,
  },
  {
    key: 'target_position_pct',
    label: () => 'Target Position %',
    step: '0.01',
    allowsZero: true,
    placeholder: 'e.g. 2',
    sourced: snapshot => snapshot?.desk_mandate?.target_position_pct?.value,
    format: value => formatPercentage(value, 2),
  },
];

const emptyValues: OverridesFormValues = {
  limit_price: '',
  limit_price_note: '',
  mandate_qty: '',
  mandate_qty_note: '',
  target_position_pct: '',
  target_position_pct_note: '',
};

/** Seed the form from the overrides already stored — never from the sourced figures.
 *  Pre-filling a sourced number would turn merely opening this dialog into typing every
 *  figure in by hand. */
const toFormValues = (snapshot: BlockTradeSnapshot | null): OverridesFormValues => {
  const stored = snapshot?.manual_overrides ?? {};
  return OVERRIDABLE_FIELDS.reduce<OverridesFormValues>((values, field) => {
    const override = stored[field.key];
    return {
      ...values,
      [field.key]: override ? String(override.value) : '',
      [`${field.key}_note`]: override?.note ?? '',
    };
  }, emptyValues);
};

const BlockTradeOverridesModal: React.FC<BlockTradeOverridesModalProps> = ({
  open,
  blockTrade,
  onClose,
  onSaved,
}) => {
  const [formError, setFormError] = useState<string | null>(null);
  const snapshot = asSnapshot(blockTrade.additional_details);
  const isOurBuy = blockTrade.our_side === 'BUY';

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<OverridesFormValues>({ defaultValues: emptyValues });

  // Keyed on the seeded values themselves, not on `blockTrade`'s reference: the form
  // re-seeds whenever the stored overrides actually differ, whether the parent replaced
  // the trade object or mutated it in place. All six fields are flat strings, so the
  // serialised key round-trips losslessly and is also the effect's only input.
  const seedKey = JSON.stringify(toFormValues(snapshot));

  useEffect(() => {
    if (!open) return;
    setFormError(null);
    reset(JSON.parse(seedKey) as OverridesFormValues);
  }, [open, seedKey, reset]);

  const applyServerErrors = (error: unknown) => {
    if (!axios.isAxiosError(error) || !error.response) {
      setFormError('Failed to save. Please try again.');
      return;
    }
    const data = error.response.data as Record<string, unknown> | undefined;
    let matched = false;
    OVERRIDABLE_FIELDS.forEach(({ key }) => {
      const message = data?.[key];
      if (!message) return;
      matched = true;
      setError(key, {
        type: 'server',
        message: Array.isArray(message) ? String(message[0]) : String(message),
      });
    });
    if (!matched) {
      const detail = data?.detail ?? data?.non_field_errors;
      setFormError(
        typeof detail === 'string' ? detail : 'Failed to save. Please try again.',
      );
    }
  };

  const submit = handleSubmit(async values => {
    setFormError(null);

    // The endpoint is a PUT over the complete set, so a field left blank is sent as null
    // and clears its override — the sourced figure shows through again.
    const payload: BlockTradeOverridePayload = OVERRIDABLE_FIELDS.reduce((body, { key }) => {
      const raw = values[key].trim();
      const note = values[`${key}_note` as keyof OverridesFormValues].trim();
      return { ...body, [key]: raw === '' ? null : { value: raw, ...(note ? { note } : {}) } };
    }, {} as BlockTradeOverridePayload);

    try {
      onSaved(await blockTradeApi.setManualOverrides(blockTrade.id, payload));
    } catch (error) {
      applyServerErrors(error);
    }
  });

  return (
    <Modal
      open={open}
      title="Edit Desk Inputs"
      onClose={onClose}
      isSubmitting={isSubmitting}
      maxWidth={640}
      titleId="block-trade-overrides-title"
      footer={
        <>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            form="block-trade-overrides-form"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving…' : 'Save & Recalculate'}
          </Button>
        </>
      }
    >
      <form
        id="block-trade-overrides-form"
        className={styles.form}
        onSubmit={submit}
        noValidate
      >
        {formError && <div className={styles['form-error']}>{formError}</div>}

        <div className={`${styles.field} ${styles['field-full']}`}>
          <span className={styles.hint}>
            Values you enter here replace the sourced figures and are marked
            <strong> Manually entered</strong> on the detail page. The mandate value is
            recalculated from them. Leave a box empty to go back to the sourced figure.
          </span>
        </div>

        {OVERRIDABLE_FIELDS.map(field => {
          const sourced = field.sourced(snapshot);
          return (
            <React.Fragment key={field.key}>
              <div className={styles.field}>
                <label htmlFor={`override-${field.key}`}>{field.label(isOurBuy)}</label>
                <input
                  id={`override-${field.key}`}
                  type="number"
                  step={field.step}
                  min="0"
                  placeholder={field.placeholder}
                  className={errors[field.key] ? styles['error-input'] : undefined}
                  disabled={isSubmitting}
                  {...register(field.key, {
                    validate: value => {
                      if (value.trim() === '') return true;
                      const number = Number(value);
                      const ok =
                        Number.isFinite(number) &&
                        (number > 0 || (number === 0 && field.allowsZero));
                      if (ok) return true;
                      return field.allowsZero
                        ? 'Cannot be negative.'
                        : 'Must be greater than zero.';
                    },
                  })}
                />
                <span className={styles.hint}>
                  {sourced == null
                    ? 'System could not source this figure.'
                    : `System figure: ${field.format(sourced)}`}
                </span>
                {errors[field.key] && (
                  <span className={styles['error-text']}>{errors[field.key]?.message}</span>
                )}
              </div>

              <div className={styles.field}>
                <label htmlFor={`override-${field.key}-note`}>Reason (optional)</label>
                <input
                  id={`override-${field.key}-note`}
                  type="text"
                  maxLength={500}
                  placeholder="Why this number?"
                  disabled={isSubmitting}
                  {...register(`${field.key}_note` as keyof OverridesFormValues)}
                />
              </div>
            </React.Fragment>
          );
        })}
      </form>
    </Modal>
  );
};

export default BlockTradeOverridesModal;
